import { Alert, Badge, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './collection-pages.index.module.css'
import { api, type CollectionPage } from '@/lib/api'

export const Route = createFileRoute('/collection-pages/')({
  component: CollectionPagesIndex,
})

function CollectionPagesIndex() {
  const [pages, setPages] = useState<CollectionPage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPages = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await api.listCollectionPages()
        setPages(response.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load collection pages')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchPages()
  }, [])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <p>Loading collection pages...</p>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>
  }

  if (pages.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className={styles.emptyState}>
            <div className={styles.emptyIconCircle}>
              <span className={styles.emptyIconEmoji}>&#128196;</span>
            </div>
            <h3 className={styles.emptyTitle}>No collection pages yet</h3>
            <p className={styles.emptyDescription}>
              Collection pages are shareable links where your customers can submit testimonials.
              Create your first page to start gathering social proof for your business.
            </p>
            <div className={styles.emptyActions}>
              <Link to="/collection-pages/new">
                <Button variant="primary">Create Collection Page</Button>
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <div className={styles.header}>
        <p className={styles.subtitle}>
          {pages.length} collection page{pages.length === 1 ? '' : 's'}
        </p>
        <Link to="/collection-pages/new">
          <Button variant="primary">Create Collection Page</Button>
        </Link>
      </div>

      <div className={styles.grid}>
        {pages.map((page) => (
          <Link
            key={page.id}
            to="/collection-pages/$id"
            params={{ id: page.id }}
            style={{ textDecoration: 'none' }}
          >
            <Card className={styles.pageCard}>
              <CardBody>
                <div className={styles.cardHeader}>
                  <h3 className={styles.pageName}>{page.name}</h3>
                  <Badge variant="success">Active</Badge>
                </div>
                <div className={styles.cardMeta}>
                  <div className={styles.metaRow}>
                    <span className={styles.metaIcon}>&#128279;</span>
                    <span className={styles.slug}>/{page.slug}</span>
                  </div>
                  <div className={styles.metaRow}>
                    <span className={styles.metaIcon}>&#128197;</span>
                    <span>Created {formatDate(page.createdAt)}</span>
                  </div>
                  {page.headline && (
                    <div className={styles.metaRow}>
                      <span className={styles.metaIcon}>&#128172;</span>
                      <span>{page.headline}</span>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Link>
        ))}
      </div>
    </>
  )
}
